// What a setting's value may be, per type. Shared by resolve, extendRegistry and the validators (internal module:
// other tracks use the package through index.js only).
/** @typedef {import('./typedefs.js').SettingEntry} SettingEntry */
/** @typedef {import('./typedefs.js').Registry} Registry */

export const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
export const clone = (v) => (v && typeof v === 'object' ? JSON.parse(JSON.stringify(v)) : v);
/** A dotted path inside plain data, own properties only (`''` = the value itself). */
export const at = (o, path) => (path ? path.split('.').reduce((v, k) => (v != null && Object.hasOwn(v, k) ? v[k] : undefined), o) : o);

/**
 * Delivery 2: a compiled bundle's `settings` holds the map's resort-level values (dotted keys, as before) AND the
 * package's additions to the settings list under these seven names, the fields of settings.json, so a published
 * page needs no settings.json: `extendRegistry(bundle.settings)`. No setting key can be one of them (keys are dotted).
 */
export const PACKAGE_FIELDS = ['schema', 'settings', 'controls', 'looks', 'auroraLevels', 'layout', 'strings'];
/** The resort-level values of a bundle's `settings`: everything but the package fields. */
export const valuesOnly = (settings) => (isObj(settings) ? Object.fromEntries(Object.entries(settings).filter(([k]) => !PACKAGE_FIELDS.includes(k))) : {});

/**
 * The entitlement this VALUE of a setting needs, or null: a gate is needed only by a value that switches the gated
 * thing ON. A gated setting (`gate`) needs it for every value but its off value (`offOf`); a gated option
 * (`gateValues`) needs its own, the other options none. Switching a gated thing off never needs the entitlement.
 * A detail of an add-on (`gateSwitch`: the clouds' weather, amount, speed, look under `clouds.enabled`) needs it
 * only while the add-on is switched on in the same document: with `values` given and its switch not `true` there,
 * the detail shows nothing and needs nothing. A preset that switched clouds off keeps its old weather, and that
 * must not block Go live (lesson 2026-10-17-switched-off-addon-details). Without `values` the detail is judged alone.
 * VIS-128: an option named `off` of a gated setting is an off value too (the clouds' weather "Off" is a clear sky,
 * while the default is "auto"): picking it switches the gated thing off, so a client without the switch may pick it.
 * @param {SettingEntry | null | undefined} e
 * @param {any} v
 * @param {{ [key: string]: any }} [values]  the document the value sits in (a preset's or a placement's values)
 * @returns {string | null}
 */
export const gateFor = (e, v, values) => (e?.gate ? (v === 'off' || JSON.stringify(v) === JSON.stringify(offOf(e)) || (!!e.gateSwitch && isObj(values) && values[e.gateSwitch] !== true) ? null : e.gate) : (typeof v === 'string' && e?.gateValues && Object.hasOwn(e.gateValues, v) ? e.gateValues[v] : null));
/**
 * A gated setting switched off, what `resolve` gives a map without the entitlement: `false` for a switch; for any
 * other type the entry's default (a gated setting that is not a switch declares the state WITHOUT the entitlement
 * as its default, and a gated option is left for the default, which is not one).
 */
export const offOf = (e) => (e.type === 'bool' ? false : clone(e.default));

export const SEASONS = ['winter', 'spring', 'summer', 'autumn']; // config.js SEASON_NAMES
const B32 = '[0-9a-hjkmnp-tv-z]{16}'; // ids.js: 16 chars of lowercase Crockford base32
export const RE = {
  key: /^[a-z0-9][a-z0-9-]{0,79}$/, // place, unit and category keys (assignments.schema.json)
  slug: /^[a-z0-9-]{2,40}$/,
  lang: /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/,
  color: /^#([0-9a-f]{3}|[0-9a-f]{6})$/i,
  ref: /^[a-z]{3}_[0-9a-z-]{1,40}$/, // a reference to a public id, read leniently (`tur_legacy` is one)
  pre: new RegExp(`^pre_${B32}$`), tur: new RegExp(`^tur_${B32}$`), lyr: new RegExp(`^lyr_${B32}$`),
  ftr: new RegExp(`^ftr_${B32}$`), map: new RegExp(`^map_${B32}$`), ast: new RegExp(`^ast_${B32}$`),
};

const DAYS = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
/** `"6-21"` or `"06-21"` as `"06-21"`; null when it is not a day of the year. */
export function monthDay(v) {
  const m = typeof v === 'string' ? /^(\d{1,2})-(\d{1,2})$/.exec(v) : null;
  const mo = m ? +m[1] : 0, d = m ? +m[2] : 0;
  return mo >= 1 && mo <= 12 && d >= 1 && d <= DAYS[mo - 1] ? `${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}` : null;
}

/** A translatable text (contract section 6): a string, or texts keyed by language tag (+ `_auto`). */
const str = (s) => typeof s === 'string' && s.length <= 4000;
export const isText = (v) => str(v) || (isObj(v) && Object.entries(v).every(([k, s]) => (k === '_auto' ? Array.isArray(s) && s.every(str) : RE.lang.test(k) && str(s))));

const vec3 = (a) => Array.isArray(a) && a.length === 3 && a.every(Number.isFinite);
export const isView = (v) => isObj(v) && vec3(v.position) && vec3(v.target) && (v.fov === undefined || (Number.isFinite(v.fov) && v.fov >= 5 && v.fov <= 120))
  && (v.lookUp === undefined || (Number.isFinite(v.lookUp) && v.lookUp >= 0 && v.lookUp <= 90));

const idOf = (x) => x?.id;
/**
 * The values an enum accepts on one map: the engine's, plus the map's own from the table `valuesFrom` names.
 * @param {SettingEntry} e
 * @param {Registry} reg
 * @param {{ resort?: object, bundle?: object }} [ctx]
 * @returns {string[]}
 */
export function options(e, reg, ctx = {}) {
  const t = e.valuesFrom;
  const more = t === 'looks' ? reg.looks.map(idOf) : t === 'auroraLevels' ? reg.auroraLevels.map(idOf) : t === 'layouts' ? Object.keys(reg.layouts)
    : t === 'snowLevels' ? (at(ctx.resort, 'vegetation.snow.levels') || []).map(idOf) : t === 'langs' ? ctx.bundle?.langs || [] : [];
  return [...(e.values || []), ...more];
}

// what one id of an `ids` setting, and one field of a `list` item, may be
const ITEM = { season: (v) => SEASONS.includes(v), lang: (v) => RE.lang.test(v), tour: (v) => RE.ref.test(v), layer: (v) => RE.ref.test(v),
  place: (v) => /^[A-Za-z0-9][A-Za-z0-9-]{0,79}$/.test(v), category: (v) => RE.key.test(v) }; // delivery 3: a place, unit or unit type key (a type id may carry capitals); a category id
const FIELD = { number: Number.isFinite, text: isText, season: ITEM.season, monthDay: (v) => !!monthDay(v) };
/**
 * Fits a value to a setting's type: `{ ok: true, value }` (a number brought into its range with `clamped: true`,
 * a day padded, lists and objects copied) or `{ ok: false, why }`. `doc` values are opaque and passed by reference.
 * @param {SettingEntry} e
 * @param {any} v
 * @param {Registry} reg
 * @param {{ resort?: object, bundle?: object }} [ctx]
 * @returns {{ ok: boolean, value?: any, clamped?: boolean, why?: string }}
 */
export function check(e, v, reg, ctx) {
  const no = (why) => ({ ok: false, why }), yes = (value) => ({ ok: true, value });
  if (v === undefined) return no('no value');
  if (v === null) return e.default === null ? yes(null) : no('may not be empty');
  switch (e.type) {
    case 'bool': return typeof v === 'boolean' ? yes(v) : no('is true or false');
    case 'enum': return typeof v === 'string' && options(e, reg, ctx).includes(v) ? yes(v) : no('is not one of its values');
    case 'number': {
      if (typeof v !== 'number' || !Number.isFinite(v)) return no('is a number');
      const n = Math.min(e.max ?? Infinity, Math.max(e.min ?? -Infinity, v));
      return { ok: true, value: n, clamped: n !== v };
    }
    case 'text': return isText(v) ? yes(clone(v)) : no('is a text, or texts by language');
    case 'color': return typeof v === 'string' && RE.color.test(v) ? yes(v) : no('is a hex colour');
    case 'monthDay': { const d = monthDay(v); return d ? yes(d) : no('is a day as MM-DD'); }
    case 'place': return typeof v === 'string' && RE.key.test(v) ? yes(v) : no('is a place key');
    case 'view': return isView(v) ? yes(clone(v)) : no('is a view { position, target, fov, lookUp }');
    case 'ids': return Array.isArray(v) && v.every((x) => typeof x === 'string' && (ITEM[e.of] || (() => true))(x)) ? yes([...new Set(v)]) : no(`is a list of ${e.of || 'id'}s`);
    case 'list': return Array.isArray(v) && v.every((x) => isObj(x) && Object.entries(e.item || {}).every(([k, t]) => x[k] === undefined || !FIELD[t] || FIELD[t](x[k])))
      ? yes(clone(v)) : no('is a list of items');
    default: return e.format && typeof v !== 'string' ? no(`is a ${e.format}`) : yes(v);
  }
}
